import { useState, useEffect } from "react";
import NavbarSidebarWrapper from "../../../components/navbarSidebarWrapper";
import "./home.css";
import { useNavigate } from "react-router-dom";
import { mapMatchToAppFormat } from "../../../services/cricketAPI";
import actions from "../../../redux/actions";
import { useSelector } from "react-redux";
import { useToast } from "../../../components/ToastContext";
import { saveSelectedMatch } from "../../../utils/playerSession";

// Date Helpers
const getJustDate = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const getJustHours = (dateStr) =>
  new Date(dateStr).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
  });

export function Home() {
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Redux selector (safe optional chaining)
  const currentUpcomingRaw = useSelector(
    (state) => state.fantacySlice?.FnCurrentUpcommingList?.Result,
  );
  const currentUpcomingList = Array.isArray(currentUpcomingRaw) ? currentUpcomingRaw : [];
  const clientCompletedRaw = useSelector(
    (state) => state.fantacySlice?.FnClientCompletedList?.Result,
  );
  const clientCompletedList = Array.isArray(clientCompletedRaw) ? clientCompletedRaw : [];

  const [pastMatches, setPastMatches] = useState([]);
  const [liveMatches, setLiveMatches] = useState([]);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  // Fantasy Rules HTML Content
  const rulesHTML = `
    <div style="font-family: Arial, sans-serif; line-height: 1.6; margin: 20px;">
      <h1>🏏 Fantasy Cricket Rules & Points System</h1>

      <div style="margin-bottom: 25px;">
        <h2>General Rules</h2>
        <ul>
          <li><strong>Company Decision:</strong> The company's decision will be final in all matters.</li>
          <li><strong>Team Creation & Updates:</strong> Users can create or update their team until the toss/match start.</li>
          <li><strong>Prize Money Distribution:</strong>
            <ul>
              <li>1st Place: 50%</li>
              <li>2nd Place: 30%</li>
              <li>3rd Place: 10%</li>
            </ul>
          </li>
          <li><strong>Player Credits/Rates:</strong> Player credit/rate will be set based on current performance.</li>
        </ul>
      </div>

      <div style="margin-bottom: 25px;">
        <h2>T20 Fantasy Cricket Points System</h2>

        <h3>Batting Points</h3>
        <ul>
          <li><strong>Base Points:</strong>
            <ul>
              <li>1 point per run</li>
              <li>1 point per four</li>
              <li>2 points per six</li>
            </ul>
          </li>
          <li><strong>Milestone Bonuses:</strong>
            <ul>
              <li>Century (100+ runs): +16 points</li>
              <li>Half-century (50+ runs): +8 points</li>
              <li>30 runs: +4 points</li>
            </ul>
          </li>
          <li><strong>Duck Penalty:</strong> If a batsman/all-rounder/keeper scores 0 and is out: –2 points</li>
          <li><strong>Strike Rate Bonus (min 10 balls faced):</strong>
            <ul>
              <li>SR &gt; 170: +6 points</li>
              <li>SR ≥ 150: +4 points</li>
              <li>SR ≥ 130: +2 points</li>
              <li>SR &lt; 50: –6 points</li>
              <li>SR ≤ 60: –4 points</li>
              <li>SR ≤ 70: –2 points</li>
            </ul>
          </li>
        </ul>

        <h3>Bowling Points</h3>
        <ul>
          <li><strong>Base Points:</strong>
            <ul>
              <li>25 points per wicket (excluding run-outs)</li>
              <li>12 points per maiden over</li>
            </ul>
          </li>
          <li><strong>Milestone Bonuses:</strong>
            <ul>
              <li>5-wicket haul: +16 points</li>
              <li>4-wicket haul: +8 points</li>
              <li>3-wicket haul: +4 points</li>
            </ul>
          </li>
          <li><strong>Economy Rate Bonus (min 2 overs bowled):</strong>
            <ul>
              <li>ER &lt; 5: +6 points</li>
              <li>ER ≤ 6: +4 points</li>
              <li>ER ≤ 7: +2 points</li>
              <li>ER &gt; 12: –6 points</li>
              <li>ER ≥ 11: –4 points</li>
              <li>ER ≥ 10: –2 points</li>
            </ul>
          </li>
        </ul>

        <h3>Other Points</h3>
        <ul>
          <li>Captain: 2× points</li>
          <li>Vice-Captain: 1.5× points</li>
        </ul>
      </div>
    </div>
  `;

  // Fetch Data
  // =======================
  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      setLoading(true);
      const [currentRes, completedRes] = await Promise.all([
        actions.getCurrentMatches(),
        actions.getClientCompleted().catch(() => null),
      ]);
      if (!completedRes?.data?.Status) {
        console.log("Completed matches: no data found");
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to fetch matches.";
      showToast(message, "error");
    } finally {
      setLoading(false);
    }
  };

  // When Redux updates → process current/upcoming matches
  // =======================
  useEffect(() => {
    if (!currentUpcomingList.length) return;

    const formatted = currentUpcomingList.map(mapMatchToAppFormat);

    const live = formatted.filter((m) => m.status === "2");
    const upcoming = formatted.filter((m) => m.status === "1");

    setLiveMatches(live);
    setUpcomingMatches(upcoming);
  }, [currentUpcomingList]);

  useEffect(() => {
    if (!clientCompletedList.length) return;

    const formatted = clientCompletedList.map(mapMatchToAppFormat);
    setPastMatches(formatted);
  }, [clientCompletedList]);

  // Loader
  // =======================
  if (loading) {
    return (
      <NavbarSidebarWrapper>
        <div className="content-wrapper text-center py-5">
          <div className="spinner-border text-light" />
          <p className="text-white mt-3">Loading matches...</p>
        </div>
      </NavbarSidebarWrapper>
    );
  }

  return (
    <NavbarSidebarWrapper>
      <div className="content-wrapper mb-4">
        <div className="content-header d-flex justify-content-between"
          onClick={() => setShowModal(true)}
          style={{ cursor: "pointer" }}>
          <span className="">🏏 Fantasy Cricket Rules & Points System</span>
        </div>
        {liveMatches.length === 0 && upcomingMatches.length === 0 && pastMatches.length === 0 && (
          <p className="text-center text-white-50 mt-4">No data found</p>
        )}
        {/* ================= LIVE ================= */}
        {liveMatches.length > 0 && (
          <div className="my-4">
            <h3 className="text-white">Live Matches</h3>
            <div className="row g-3">
              {liveMatches.map((u) => (
                <div
                  key={u.id}
                  className="col-12 col-md-6"
                  onClick={() => {
                    saveSelectedMatch(u);
                    navigate(`/contests/${u.matchId}`);
                  }}
                >
                  <MatchCard match={u} status="LIVE" />
                </div>
              ))}
            </div>
          </div>
        )}
        {/* ================= UPCOMING ================= */}
        {upcomingMatches.length > 0 && (
          <div>
            <h3 className="text-white">Upcoming Matches</h3>
            <div className="row g-3">
              {upcomingMatches.map((u) => (
                <div
                  key={u.id}
                  className="col-12 col-md-6"
                  onClick={() => {
                    saveSelectedMatch(u);
                    navigate(`/contests/${u.matchId}`);
                  }}
                >
                  <MatchCard match={u} status="UPCOMING" />
                </div>
              ))}
            </div>
          </div>
        )}
        {/* ================= COMPLETED ================= */}
        {pastMatches.length > 0 && (
          <div className="mb-4">
            <h3 className="text-white">Completed Matches</h3>

            <div className="row g-3">
              {pastMatches.map((u) => (
                <div
                  key={u.id}
                  className="col-12 col-md-6"
                  onClick={() => {
                    saveSelectedMatch(u);
                    navigate(`/complete-contests/${u.matchId}`);
                  }}
                >
                  <MatchCard match={u} status="COMPLETED" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Fantasy Rules Modal */}
      {showModal && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">🏏 Fantasy Cricket Rules & Points System</h5>
                  <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                </div>
                <div className="modal-body">
                  <div dangerouslySetInnerHTML={{ __html: rulesHTML }} />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" onClick={() => setShowModal(false)}></div>
        </>
      )}
    </NavbarSidebarWrapper>
  );
}

// Reusable Card Component
// =======================
const MatchCard = ({ match, status }) => (
  <div className="bg-light p-3 match-card">
    <div className="d-flex justify-content-between align-items-center">
      <div>
        <div className="d-flex align-items-center mb-2">
          <img src={match.teamAwayFlagUrl} alt="" className="team-flag" />
          <span className="ms-2">{match.away.name}</span>
        </div>

        <div className="d-flex align-items-center">
          <img src={match.teamHomeFlagUrl} alt="" className="team-flag" />
          <span className="ms-2">{match.home.name}</span>
        </div>
      </div>

      <div className="text-end">
        <div className="fw-bold small">{status}</div>
        <div className="small text-muted">{getJustDate(match.date)}</div>
        <div className="small text-muted">{getJustHours(match.date)}</div>
      </div>
    </div>
  </div>
);

export default Home;
